import axios from 'axios';
import { createCustomError } from '../utils/createCustomError';
import { get, set } from '../utils/storage';
import { FEATURE_FLAGS, isFeatureEnabled } from '../utils/featureFlag';

const { REACT_APP_API_URL: apiUrl } = process.env;

const instance = axios.create({
  baseURL: apiUrl,
  withCredentials: true
});

const httpStatusCode = {
  Unauthorized: 401
};

const axiosErrorCode = {
  NetworkError: 'ERR_NETWORK'
};

instance.interceptors.request.use((config) => {
  const entra = get('msal.token.storage');
  
  // Check if token is expired before making the request
  if (entra?.accessToken && entra?.expiresOn) {
    const now = new Date().getTime();
    const expiry = new Date(entra.expiresOn).getTime();
    const isExpired = now >= expiry;
    const isNearExpiry = (expiry - now) < 5 * 60 * 1000; // Expires in less than 5 minutes
    
    if (isExpired) {
      // Token is expired, will trigger refresh on 401
    } else if (isNearExpiry) {
      // Token expires soon, consider preemptive refresh
    }
  }
  
  config.headers['x-access-token'] = entra?.accessToken ?? null;
  
  return config;
});

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.code === axiosErrorCode.NetworkError) {
      throw createCustomError(error.message, 'NetworkError');
    }

    // Handle 401 errors with automatic token refresh for Entra
    if (error.response?.status === httpStatusCode.Unauthorized && isFeatureEnabled(FEATURE_FLAGS.ENTRA)) {
      try {
        // Get MSAL instance from global scope
        const msalInstance = window.msalInstance;
        if (!msalInstance) {
          throw createCustomError(error.response?.data?.message ?? error.message, 'UnauthorizedError');
        }

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) {
          throw createCustomError(error.response?.data?.message ?? error.message, 'UnauthorizedError');
        }

        // Import scopes dynamically
        const { loginScopes } = await import('../config/config');
        
        // Use basic login scopes to get ID token with force refresh
        const response = await msalInstance.acquireTokenSilent({
          scopes: loginScopes,
          account: accounts[0],
          forceRefresh: true // Always force refresh on 401 errors
        });

        // Use ID token for backend authentication
        const tokenForBackend = response.idToken || response.accessToken;

        if (!tokenForBackend) {
          throw new Error('No token received from refresh');
        }

        set('msal.token.storage', {
          accessToken: tokenForBackend,
          expiresOn: response.expiresOn,
          scopes: response.scopes,
          tokenType: response.idToken ? 'id_token' : 'access_token'
        });
        
        // Retry the original request with the new token
        const originalRequest = error.config;
        const refreshedToken = get('msal.token.storage');
        originalRequest.headers['x-access-token'] = refreshedToken?.accessToken;
        
        return axios(originalRequest);
        
      } catch (refreshError) {
        // Fall through to normal 401 handling
      }
    }

    if (error.response?.status === httpStatusCode.Unauthorized) {
      throw createCustomError(error.response?.data?.message ?? error.message, 'UnauthorizedError');
    }

    if (error.name === 'AxiosError') {
      throw createCustomError(
        error.response?.data?.message ?? error.message,
        error.response?.data?.name ?? error.name
      );
    }

    throw error;
  }
);

export default instance;

export const handleError = (err, callbacks) => {
  return callbacks[err?.name] ? callbacks[err?.name]() : callbacks.default();
};
